import { inject } from '@angular/core';
import { InstallationRefusal, toRefusal } from '@core/helpers/installation-refusal.helper';
import {
  FindingClass,
  FindingResolution,
  FindingSubjectType,
  MigrationFindingPage,
  MigrationReportFilter,
  MigrationReportSummary,
} from '@core/models/migration-report.model';
import { MigrationReportService } from '@core/services/migration-report.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

export const MIGRATION_REPORT_PAGE_SIZE = 25;

type MigrationReportState = {
  isLoadingSummary: boolean;
  summary: MigrationReportSummary | null;
  summaryError: InstallationRefusal | null;
  isLoading: boolean;
  page: MigrationFindingPage;
  filter: MigrationReportFilter;
  loadError: InstallationRefusal | null;
};

const initialState: MigrationReportState = {
  isLoadingSummary: false,
  summary: null,
  summaryError: null,
  isLoading: false,
  page: { numberOfItems: 0, findings: [] },
  // The triage backlog: what nobody has decided on yet.
  filter: { resolution: 'open', limit: MIGRATION_REPORT_PAGE_SIZE, offset: 0 },
  loadError: null,
};

export interface MigrationReportFilterChange {
  findingClass?: FindingClass;
  subjectType?: FindingSubjectType;
  resolution?: FindingResolution;
  partnerOid?: string;
}

// Provided by the report page, so the summary and the page of findings never outlive it.
export const MigrationReportStore = signalStore(
  withState(initialState),
  withMethods((store, service = inject(MigrationReportService)) => {
    const load = rxMethod<MigrationReportFilter>(
      pipe(
        tap(filter => patchState(store, { isLoading: true, filter, loadError: null })),
        switchMap(filter =>
          service.list(filter).pipe(
            tapResponse({
              next: page => patchState(store, { page, isLoading: false }),
              error: (error: unknown) =>
                patchState(store, {
                  page: initialState.page,
                  isLoading: false,
                  loadError: toRefusal(error),
                }),
            }),
          ),
        ),
      ),
    );

    return {
      loadSummary: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoadingSummary: true, summaryError: null })),
          switchMap(() =>
            service.summary().pipe(
              tapResponse({
                next: summary => patchState(store, { summary, isLoadingSummary: false }),
                error: (error: unknown) =>
                  patchState(store, {
                    summary: null,
                    isLoadingSummary: false,
                    summaryError: toRefusal(error),
                  }),
              }),
            ),
          ),
        ),
      ),

      // A change of filter starts again from the first page; an omitted resolution is the backlog.
      applyFilter: (change: MigrationReportFilterChange) =>
        load({
          ...change,
          resolution: change.resolution ?? initialState.filter.resolution,
          limit: store.filter().limit,
          offset: 0,
        }),

      loadPage: (pageIndex: number) =>
        load({ ...store.filter(), offset: pageIndex * store.filter().limit }),

      reload: () => load({ ...store.filter() }),
    };
  }),
);
